import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiResponse } from 'further-types/api';
import { Api, AccountType } from 'further-types/investor';
import { api } from 'lib/httpClient';
import { useNotification } from 'hooks/ui/useNotification';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';
import { ErrorMessages } from 'further-ui/labels';
import trpcApi from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';

type Options = {
  onExportInvestorStatement?: (data: object, date?: Date) => void;
};

function useUpdateInvestor(investorId?: string) {
  const utils = trpcApi.useUtils();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    throwOnError: false,
    mutationFn: async (data: Api.UpdateInvestorRequest) => {
      const path =
        data.accountType === AccountType.LegalEntity
          ? `investor/update/${investorId}/legal-entity`
          : `investor/update/${investorId}`;

      const { data: result } = await api.put<
        ApiResponse<Api.UpdateInvestorResponse>
      >(path, data);
      return result;
    },
    onSuccess: async () => {
      if (!investorId) return;
      notification.success('Investor has been updated.');
      utils.investor.getInvestor.invalidate({ investorId });
      utils.investor.listInvestors.invalidate();
      utils.investorAccessGrant.list.invalidate();
      await queryClient.invalidateQueries({
        queryKey: ['investor-change-log', investorId],
      });
    },
    onError: (error) => {
      const errorMessage = (error as AxiosError<ApiResponse>).response?.data
        ?.responseMsg;
      if (errorMessage?.includes('duplicate')) {
        notification.warning(errorMessage);
      } else {
        notification.error(errorMessage ?? 'Something went wrong.');
      }
    },
  });
}

function useCreateInvestor() {
  const notification = useNotification();

  const mutationOptions = useHandleTrpcMutation<{ _id: string }>()({
    successMessage: 'Investor has been created.',
    invalidations: [
      getQueryKey(trpcApi.investor.listInvestors, undefined, 'query'),
      ['investors-lean'],
    ],
    onError: (err) => {
      const errorMessage = err?.data?.cause ?? err?.message;
      if (errorMessage?.includes('duplicate')) {
        notification.warning(errorMessage);
      } else {
        notification.error(errorMessage ?? 'Something went wrong.');
      }
    },
  });

  const createInvestorMutation =
    trpcApi.investor.createInvestor.useMutation(mutationOptions);
  const createLegalEntityMutation =
    trpcApi.investor.createLegalEntity.useMutation(mutationOptions);

  return {
    createInvestor: createInvestorMutation.mutateAsync,
    createLegalEntity: createLegalEntityMutation.mutateAsync,
    isPending:
      createInvestorMutation.isPending || createLegalEntityMutation.isPending,
  };
}

function useSetDefaultAdviser(investorId?: string) {
  const utils = trpcApi.useUtils();
  const notification = useNotification();

  return useMutation({
    throwOnError: false,
    mutationFn: async (data: Api.SetDefaultAdviserRequest) => {
      const { data: result } = await api.put<ApiResponse<unknown>>(
        `investor/set-default-adviser/${investorId}`,
        data,
      );
      return result;
    },
    onSuccess: async () => {
      if (!investorId) return;
      notification.success('Default adviser has been updated.');
      utils.investor.getInvestor.invalidate({ investorId });
      utils.investor.listInvestors.invalidate();
    },
    onError: (error) => {
      const errorMessage = (error as AxiosError<ApiResponse>).response?.data
        ?.responseMsg;
      notification.error(errorMessage ?? 'Something went wrong.');
    },
  });
}

function useExportInvestorStatement(
  investorId?: string,
  firmId?: string,
  onExport?: Options['onExportInvestorStatement'],
) {
  const handleApiException = useApiExceptionHandler();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (valuationDate?: Date) => {
      const response = await api.post(
        'investor/get-investor-statements',
        { investorId, firmId, valuationDate },
        { responseType: 'blob' },
      );

      return response.data;
    },
    onSuccess: async (data, valuationDate) => {
      onExport?.(data, valuationDate); // pass back valuation date for filename
    },
    onError: async (error: AxiosError) => {
      if (error?.response?.status === 404) {
        notification.error(ErrorMessages.NoInvestmentsFoundForDate);
      } else {
        handleApiException(error);
      }
    },
  });
}

export function useInvestor(
  investorId?: string,
  firmId?: string,
  { onExportInvestorStatement }: Options = {},
) {
  const {
    isPending: isUpdatingInvestor,
    mutate: updateInvestor,
    mutateAsync: updateInvestorAsync,
  } = useUpdateInvestor(investorId);
  const {
    createInvestor: createInvestorAsync,
    createLegalEntity: createLegalEntityAsync,
    isPending: isCreatingInvestor,
  } = useCreateInvestor();
  const {
    isPending: isExportInvestorStatementLoading,
    mutate: exportInvestorStatement,
  } = useExportInvestorStatement(investorId, firmId, onExportInvestorStatement);
  const { mutate: setDefaultAdviser, isPending: isSettingDefaultAdviser } =
    useSetDefaultAdviser(investorId);

  return {
    isUpdatingInvestor,
    isCreatingInvestor,
    updateInvestor,
    updateInvestorAsync,
    createInvestorAsync,
    createLegalEntityAsync,
    exportInvestorStatement,
    setDefaultAdviser,
    isExportInvestorStatementLoading,
    isSettingDefaultAdviser,
  };
}
